import React from 'react';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useVoicePlayback } from './VoicePlaybackContext';

type ReadAloudButtonProps = {
  id: string;
  text: string;
  alreadyNormalized?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  size?: 'small' | 'medium';
};

const ReadAloudButton: React.FC<ReadAloudButtonProps> = ({
  id,
  text,
  alreadyNormalized = false,
  disabled = false,
  ariaLabel = 'Read aloud',
  size = 'small',
}) => {
  const { activeId, status, error, toggle, stop } = useVoicePlayback();
  const isActive = activeId === id;
  const isLoading = isActive && status === 'loading';
  const isPlaying = isActive && status === 'playing';
  const isPaused = isActive && status === 'paused';
  const isError = isActive && status === 'error';

  const handleClick = async () => {
    if (!text.trim() || disabled) return;
    if (isError) {
      stop();
    }
    await toggle({ id, text, alreadyNormalized });
  };

  let icon = <VolumeUpIcon fontSize="small" />;
  let label = ariaLabel;
  if (isLoading) {
    icon = <CircularProgress size={18} color="inherit" />;
    label = 'Loading audio';
  } else if (isPlaying) {
    icon = <PauseIcon fontSize="small" />;
    label = 'Pause reading';
  } else if (isPaused) {
    icon = <PlayArrowIcon fontSize="small" />;
    label = 'Resume reading';
  } else if (isError) {
    icon = <ErrorOutlineIcon fontSize="small" />;
    label = error || 'Retry read aloud';
  }

  return (
    <Tooltip title={isError && error ? error : label}>
      <span>
        <IconButton
          type="button"
          size={size}
          onClick={handleClick}
          disabled={disabled || !text.trim() || isLoading}
          aria-label={label}
          sx={{
            width: 44, height: 44,
            color: isError ? 'error.main' : 'primary.main',
            bgcolor: isPlaying || isPaused ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default ReadAloudButton;
